import type { Topic } from "@foundation/artemis-client";
import { QuoteData, RealTimeData } from "~types/types";
import isMarketOpen from "~transforms/isMarketOpen";
import priceUpdateDateTime from "~transforms/priceUpdateDateTime";
import { formatDecimals } from "~utils/portfolio/formatNumber";

type MappedField = {
  field?: string;
  transform: (v: any, realTimeData?: RealTimeData) => any;
};

type FieldFormatter = { [key: string]: MappedField };

const noTransform = (v: unknown) => v;
const numberWithCommas = (n: number) => Number(n).toLocaleString("en-US");
const numberTwoDecimals = (n: number) => formatDecimals(n, 2);

const numberToMarketCap = (n: number) =>
  new Intl.NumberFormat("en-US", {
    maximumFractionDigits: 3,
    notation: "compact",
    compactDisplay: "short",
  }).format(n);

const FIELD_FORMATTERS: FieldFormatter = {
  averageVolume30Day: { transform: numberWithCommas },
  exchangeDelay: { transform: noTransform },
  exchangeId: { transform: noTransform },
  highPrice52Week: { transform: noTransform },
  highPrice: { transform: noTransform },
  isExchangeDelayed: { transform: noTransform },
  lastDividendReported: { transform: numberWithCommas },
  lastPostSessionPrice: { transform: noTransform },
  priceLastUpdated: {
    field: "lastUpdate",
    transform: priceUpdateDateTime,
  },
  lowPrice52Week: { transform: noTransform },
  lowPrice: { transform: noTransform },
  marketStatus: { transform: noTransform },
  openPrice: { transform: numberTwoDecimals },
  percentChange1Day: { transform: noTransform },
  percentChange1DayPostRegular: { transform: noTransform },
  previousClosingPriceOneTradingDayAgo: { transform: noTransform },
  price: { transform: numberTwoDecimals },
  priceChange1Day: { transform: noTransform },
  priceEarningsRatio: { transform: numberWithCommas },
  securityStatus: { transform: noTransform },
  sharesOutstanding: { transform: numberToMarketCap },
  volume: { transform: numberWithCommas },
  tradingDate: {
    field: "isOpen",
    transform: isMarketOpen,
  },
};

export default function formatRealTimeData(
  topic: Topic,
  quoteData: QuoteData | undefined
) {
  const rawData: RealTimeData = (topic.data ?? {}) as RealTimeData;

  // tradingDate is not received in every event from BPIPE
  // override tradingDate from wardenclyffe if absent in BPIPE event
  rawData.tradingDate =
    rawData.tradingDate ?? (quoteData?.tradingDate as string);

  return Object.keys(rawData).reduce((acc, _field) => {
    const fieldValue = rawData[_field];

    if (_field in FIELD_FORMATTERS) {
      const { field = _field, transform } = FIELD_FORMATTERS[_field];

      return {
        ...acc,
        [field]: transform(fieldValue, rawData),
      };
    }

    return acc;
  }, {});
}
